import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePostEditReport from '@/hooks/useApi/usePostEditReport';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { buildEditReportItemBasicFields } from './buildEditReportItemFields.helpers';

import { FieldData } from '@/components/Form/types';
import { ReportDetail } from '../types';
import { CircleButton } from 'UI';

import styles from './ReportDetailsPage.module.scss';

const { useForm, Box } = vp;

type EditReportItemProps = {
  report: ReportDetail;
  subscriptions?: vp.SubscriptionItem[];
  reportItemLabel: string;
  reportItemValue: string;
  setEditReport: (e: boolean) => void;
  setReportBeingEdited: (e: string) => void;
};

type FC = FunctionComponent<EditReportItemProps>;
const EditReportItem: FC = ({
  report,
  subscriptions,
  reportItemLabel,
  reportItemValue,
  setEditReport,
  setReportBeingEdited,
}: EditReportItemProps) => {
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.ReportDetails' });
  const { permissions } = usePermissions();
  const alertDialog = useAlertDialog();
  const [editReport, postEditReport] = usePostEditReport({});
  const [submitEditReport, setSubmitEditReport] = useState(false);
  const [shouldEditReport, setShouldEditReport] = useState(false);
  const basicRows: FieldData<ReportDetail>[] = buildEditReportItemBasicFields(
    subscriptions,
    reportItemLabel,
    reportItemValue,
  );
  const { renderFormElements, ctx } = useForm<ReportDetail, ReportDetail>({
    config: basicRows,
    initialValues: report,
    onSubmit: (serializedData, { resetForm }) => {
      onSubmit(serializedData);
      resetForm();
    },
  });

  const onSubmit = (values: EditReportItemProps['report']) => {
    setShouldEditReport(false);
    postEditReport({
      PortalUser: response.LoginUserName,
      SubscriptionID: report.ID,
      ReportName: values.ReportName,
      Description: values.Description,
      DateFormat: values.DateFormat,
      Frequency: values.Frequency,
    });
    setSubmitEditReport(true);
  };

  useEffect(() => {
    if (submitEditReport === true) {
      if (editReport.Success) {
        setEditReport(true);
      } else if (editReport.Success === false) {
        alertDialog.caution(t('editFailDialogue.message'), `${editReport.ErrorMessage}`, {
          confirmLabel: t('editFailDialogue.confirmLabel'),
        });
      }
    }
    setSubmitEditReport(false);
  }, [editReport]);

  useEffect(() => {
    if (shouldEditReport && report) {
      ctx.setValues(report);
    }
  }, [shouldEditReport, report]);

  return (
    <form onSubmit={ctx.handleSubmit}>
      {permissions.CreateTransactionReport && (
        <Box flx="row" ai="center" jc="sb" gap="sm">
          <div className={styles.value}>
            {shouldEditReport ? renderFormElements({ config: basicRows }) : reportItemValue}
          </div>
          {report.ScheduleType !== 'one-time' ? (
            shouldEditReport ? (
              <Box ai="center" gapSize="8px">
                <CircleButton
                  type="button"
                  colorScheme="destructive"
                  icon="close"
                  size="large"
                  onClick={() => {
                    setShouldEditReport(false);
                    setReportBeingEdited('');
                  }}
                />
                <CircleButton colorScheme="success" icon="check" size="large" type="submit" />
              </Box>
            ) : (
              <CircleButton
                type="button"
                colorScheme="action"
                icon="edit"
                onClick={() => {
                  setShouldEditReport(true);
                  setReportBeingEdited(reportItemLabel);
                }}
              />
            )
          ) : null}
        </Box>
      )}
    </form>
  );
};

export default EditReportItem;
